import { DEFAULT_LOCALE, type Locale } from '@/lib/i18n/config'

/**
 * Where Clerk lands every completed sign-in AND sign-up (ClerkProvider
 * fallbacks, the `/sign-in` and `/sign-up` pages, the marketing auth modal,
 * the public remix boxes).
 *
 * One path for both because Clerk's combined sign-in-or-up modal and its
 * organization task do not reliably pick the sign-up redirect for a new
 * account: a fresh signup landed on `/home`, painted the app shell, and only
 * then redirected to setup. `app/(auth)/continue/route.ts` decides setup vs
 * home on the server and answers with a plain redirect, so nothing paints
 * until the person reaches the right surface.
 */
export const POST_AUTH_LANDING_PATH = '/continue'

/** The landing's parameter naming the page to return to instead of `/home`. */
export const POST_AUTH_RETURN_PARAM = 'next'

/**
 * The post-auth landing for a sign-in started in `locale`: `/continue`
 * saves that language as the person's preference when they have none yet
 * (`app/(auth)/continue/route.ts`). English needs no parameter. `returnTo`
 * (a same-origin path) is where a member who needs no setup goes instead
 * of `/home`: the public remix boxes return to their template or flow.
 */
export function postAuthLandingPath(locale: Locale, returnTo?: string): string {
  const params = new URLSearchParams()
  if (locale !== DEFAULT_LOCALE) {
    params.set('locale', locale)
  }
  if (returnTo) {
    params.set(POST_AUTH_RETURN_PARAM, returnTo)
  }
  const query = params.toString()
  return query ? `${POST_AUTH_LANDING_PATH}?${query}` : POST_AUTH_LANDING_PATH
}
